import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  PhoneIcon,
  MapPinIcon,
  ShareIcon,
  CheckIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import { formatPhoneNumber } from "../../globalFunctions/PhoneNumberFormatter";
import AnanasSolutionsLogo from "../../images/AnanasSolutionsLogo.webp";
import PageHeader from "../../components/controls/PageHeader";
import { useTranslation } from "react-i18next";
import Button from "../../components/controls/Button";
import NewsGrid from "../../components/news/NewsGrid";
import NewsPagination from "../../components/news/NewsPagination";
import NewsCard from "../../components/news/NewsCard";
import StoreContent from "../Store/StoreContent";

const getCompanySocialLinks = (company) => {
  const socialLinks =
    company?.socialLinks ||
    company?.socialNetworks ||
    company?.socials ||
    company?.socialMedia ||
    [];
  return Array.isArray(socialLinks)
    ? socialLinks.filter((item) => item?.url)
    : [];
};

const TabButton = ({ active, label, onClick }) => (
  <button
    onClick={onClick}
    className={`pb-4 px-2 text-sm font-medium transition-all relative ${
      active ? "text-primary" : "text-gray-400 hover:text-gray-600"
    }`}
  >
    {label}
    {active && (
      <motion.div
        layoutId="activeTab"
        className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
      />
    )}
  </button>
);

export default function CompanyPage() {
  const { slug } = useParams();
  const [company, setCompany] = useState(null);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState("news");
  const { t } = useTranslation();

  console.log("sluuug", slug, window.location.pathname);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/company/by-slug/${slug}`)
      .then((res) => res.json())
      .then(setCompany)
      .catch(console.error);
  }, [slug]);

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const CompanyNewsSection = ({ companyId }) => {
    const pageSize = 9;
    const [news, setNews] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const loadCompanyNews = async () => {
      const url =
        `${process.env.REACT_APP_API_BASE_URL}/api/News/company?` +
        `companyId=${companyId}&page=${currentPage}&size=${pageSize}`;

      const res = await fetch(url);
      const data = await res.json();

      setNews(
        (data.items || []).map((n) => ({
          id: n.id,
          title: n.title,
          image: n.imageUrl,
          slug: n.slug,
          content: n.content,
          date: n.publishDate,
          tags: n.tagId ? [n.tagId] : [],
        })),
      );

      setTotalPages(Math.ceil((data.total || 1) / pageSize));
    };

    useEffect(() => {
      setCurrentPage(1);
    }, [companyId]);

    useEffect(() => {
      if (!companyId) return;
      loadCompanyNews();
    }, [companyId, currentPage]);

    if (!news.length) {
      return (
        <div className="py-12 text-center italic text-gray-400">
          Pas d&apos;actualités récentes.
        </div>
      );
    }

    return (
      <div className="w-full">
        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mt-10">
          {news.map((item) => (
            <div key={item.id}>
              <NewsCard item={item} />
            </div>
          ))}
        </div>

        <NewsPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>
    );
  };

  const CompanySidebar = ({ company, onCopy, copied }) => (
    <div className="space-y-6 hidden">
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
        <h4 className="font-bold text-gray-900">Coordonnées</h4>

        {company.address && company.address != "À compléter" && (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
              company.address,
            )}`}
            target="_blank"
            rel="noreferrer"
            className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors group"
          >
            <MapPinIcon className="w-5 h-5 text-primary shrink-0" />
            <span className="text-sm text-gray-600 group-hover:text-primary">
              {company.address}
            </span>
          </a>
        )}

        {company.phoneNumber && (
          <div className="flex items-center gap-3 p-3 text-sm text-gray-600">
            <PhoneIcon className="w-5 h-5 text-primary shrink-0" />
            <span>{formatPhoneNumber(company.phoneNumber)}</span>
          </div>
        )}

        <Button onClick={handleCopy} size="sm">
          <AnimatePresence mode="wait">
            {copied ? (
              <motion.span
                key="copied"
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                className="inline-flex items-center gap-2"
              >
                <CheckIcon className="w-4 h-4" /> Copié
              </motion.span>
            ) : (
              <span key="share" className="inline-flex items-center gap-2">
                <ShareIcon className="w-4 h-4" /> Partager le profil
              </span>
            )}
          </AnimatePresence>
        </Button>
      </div>
    </div>
  );

  const renderCompanyHeaderTitle = (company) => {
    if (!company) return null;
    const toAbsoluteUrl = (rawUrl) => {
      const trimmedUrl = (rawUrl || "").trim();
      if (!trimmedUrl) return "";
      if (/^https?:\/\//i.test(trimmedUrl)) return trimmedUrl;
      return `https://${trimmedUrl}`;
    };
    return (
      <div className="max-w-7xl mx-auto w-full flex flex-col md:flex-row items-center gap-6">
        <div className="transform transition-all duration-700 hover:scale-105">
          <div className="w-44 h-44 sm:w-52 sm:h-52 md:w-60 md:h-60 lg:w-72 lg:h-72 xl:w-84 xl:h-84 rounded-full bg-gradient-to-br from-[#FDB813] to-[#4CAF50] animate-pulse-slow shadow-2xl flex items-center justify-center">
            <div className="w-38 h-38 sm:w-46 sm:h-46 md:w-54 md:h-54 lg:w-[16rem] lg:h-[16rem] xl:w-[18rem] xl:h-[18rem] rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-lg">
              <img
                src={company.imageUrl}
                alt={company.name}
                className="w-full h-full object-cover rounded-full"
              />
            </div>
          </div>
        </div>

        <div className="flex-1 text-center md:text-left pt-2 md:pt-6">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 tracking-tight">
            {company.name}
          </h1>

          <div className="mt-2 flex flex-col">
            {company.address && company.address != "À compléter" && (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                  company.address,
                )}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm text-primary/90 hover:text-primary transition-all underline"
              >
                <MapPinIcon className="w-4 h-4" />
                <span className="line-clamp-1">{company.address}</span>
              </a>
            )}

            {company.phoneNumber && (
              <a
                className="inline-flex items-center gap-2 px-4 rounded-full text-sm text-primary/90 hover:text-primary transition-all underline"
                href={`tel:${company.phoneNumber}`}
              >
                <PhoneIcon className="w-4 h-4" />
                <span>{formatPhoneNumber(company.phoneNumber)}</span>
              </a>
            )}
          </div>

          <div className="mt-4 flex flex-wrap justify-center md:justify-start">
            {getCompanySocialLinks(company).map((link) => (
              <a
                key={`${link.platform || "social"}-${link.url}`}
                href={link.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 border border-white/60 shadow-sm text-sm text-gray-700 hover:text-primary hover:border-primary/30 transition-all"
              >
                <GlobeAltIcon className="w-4 h-4" />
                <span>{link.platform || "Lien"}</span>
              </a>
            ))}
          </div>

          <div className="mt-5 flex flex-wrap gap-3 justify-center md:justify-start">
            {company.website && (
              <a
                href={toAbsoluteUrl(company.website)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-6 py-2 bg-primary text-white rounded-full text-sm font-bold shadow-lg shadow-primary/20 hover:scale-105 transition-transform"
              >
                <GlobeAltIcon className="w-4 h-4" /> Visiter le site officiel
              </a>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (!company)
    return <div className="p-20 text-center text-gray-400">Chargement...</div>;

  return (
    <div className="min-h-screen bg-gray-50/30">
      <div className="  border-gray-100 relative overflow-hidden">
        <PageHeader
          logoSrc={AnanasSolutionsLogo}
          align="center"
          fullBleed={true}
          rounded={false}
          height="sm"
          gradient={{ from: "#E9FBFA", via: "#FFF9E6", to: "#FFE9EC" }}
          patternSize="32px 32px"
          patternOpacity={15}
          title={renderCompanyHeaderTitle(company)}
          subtitle={null}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="space-y-8">
          <div className="flex gap-8 border-b border-gray-100">
            <TabButton
              active={activeTab === "about"}
              label="À propos"
              onClick={() => setActiveTab("about")}
            />
            <TabButton
              active={activeTab === "products"}
              label="Produits & Services"
              onClick={() => setActiveTab("products")}
            />
            <TabButton
              active={activeTab === "news"}
              label="Actualités"
              onClick={() => setActiveTab("news")}
            />
          </div>

          <div className="max-w-none text-gray-600 leading-relaxed">
            {activeTab === "about" && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <p>
                  {company.description ||
                    "Aucune description disponible pour le moment."}
                </p>
              </motion.div>
            )}

            {activeTab === "products" && (
              <StoreContent
                companyId={company.id}
                showFilters={false}
                showCartSidebar={false}
                showFloatingCartButton={false}
              />
            )}

            {activeTab === "news" && (
              <div className="not-prose w-full max-w-none">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <CompanyNewsSection companyId={company.id} />
                </motion.div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
